import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  Alert,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Delete, PersonAdd, Save, CheckCircleOutline, Email, MarkEmailRead, Link as LinkIcon, ContentCopy, Check } from '@mui/icons-material';
import { orgAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const visibilityLabels = {
  own_only: 'Each user sees only their own requests',
  can_share: 'Users can invite others to see their requests',
  see_all: 'All members can see every request in the organization',
};

const OrganizationPanel = () => {
  const { organizations, refreshOrganizations, isAdmin } = useAuth();
  const ownerOrgs = (organizations || []).filter(
    (o) => o.my_role === 'owner' || o.my_role === 'admin'
  );
  const [activeId, setActiveId] = useState(ownerOrgs[0]?.id || '');
  const [tab, setTab] = useState(0);
  const [detail, setDetail] = useState(null);
  const [whitelist, setWhitelist] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [desc, setDesc] = useState('');
  const [contact, setContact] = useState('');
  const [visibility, setVisibility] = useState('can_share');
  const [savedAt, setSavedAt] = useState(null);

  const [newWhitelist, setNewWhitelist] = useState('');
  const [whitelistError, setWhitelistError] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inboxError, setInboxError] = useState('');

  // Invite links
  const [invites, setInvites] = useState([]);
  const [inviteLinkRole, setInviteLinkRole] = useState('member');
  const [inviteLinkError, setInviteLinkError] = useState('');
  const [copiedToken, setCopiedToken] = useState('');

  // Admin-only org creation
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgContact, setNewOrgContact] = useState('');
  const [newOrgError, setNewOrgError] = useState('');

  useEffect(() => {
    if (ownerOrgs.length > 0 && !activeId) setActiveId(ownerOrgs[0].id);
  }, [ownerOrgs, activeId]);

  useEffect(() => {
    if (!activeId) return;
    loadAll(activeId);
  }, [activeId]);

  const loadAll = async (id) => {
    setLoading(true);
    try {
      const [d, w, m, i] = await Promise.all([
        orgAPI.get(id),
        orgAPI.listWhitelist(id),
        orgAPI.listMembers(id),
        orgAPI.listInvites(id).catch(() => ({ data: [] })),
      ]);
      setDetail(d.data);
      setDesc(d.data.description || '');
      setContact(d.data.contact_email || '');
      setVisibility(d.data.visibility_mode);
      setWhitelist(w.data);
      setMembers(m.data);
      setInvites(i.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      await orgAPI.update(activeId, {
        description: desc,
        contact_email: contact || null,
        visibility_mode: visibility,
      });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
      refreshOrganizations();
    } catch (err) {
      console.error(err);
    }
  };

  const connectInbox = async () => {
    try {
      const r = await orgAPI.inboxOAuthConfig(activeId);
      if (r.data?.auth_url) {
        window.location.href = r.data.auth_url;
      } else {
        setInboxError(r.data?.message || 'Gmail OAuth is not configured.');
      }
    } catch (err) {
      setInboxError(err.response?.data?.detail || 'Unable to start Gmail OAuth.');
    }
  };

  const addWhitelistEntry = async () => {
    setWhitelistError('');
    if (!newWhitelist.trim()) return;
    try {
      await orgAPI.addWhitelist(activeId, newWhitelist.trim());
      setNewWhitelist('');
      const r = await orgAPI.listWhitelist(activeId);
      setWhitelist(r.data);
    } catch (err) {
      setWhitelistError(err.response?.data?.detail || 'Unable to add');
    }
  };

  const removeWhitelistEntry = async (entryId) => {
    try {
      await orgAPI.removeWhitelist(activeId, entryId);
      const r = await orgAPI.listWhitelist(activeId);
      setWhitelist(r.data);
    } catch (err) {
      console.error(err);
    }
  };

  const inviteMember = async () => {
    setInviteError('');
    if (!inviteEmail.trim()) return;
    try {
      await orgAPI.invite(activeId, inviteEmail.trim());
      setInviteEmail('');
      const r = await orgAPI.listMembers(activeId);
      setMembers(r.data);
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Unable to invite');
    }
  };

  const removeMember = async (userId) => {
    try {
      await orgAPI.removeMember(activeId, userId);
      const r = await orgAPI.listMembers(activeId);
      setMembers(r.data);
    } catch (err) {
      console.error(err);
    }
  };

  const generateInviteLink = async () => {
    setInviteLinkError('');
    try {
      const r = await orgAPI.createInvite(activeId, inviteLinkRole);
      setInvites((prev) => [r.data, ...prev]);
      setCopiedToken(r.data.token);
      navigator.clipboard?.writeText(r.data.invite_url || '');
      setTimeout(() => setCopiedToken(''), 2500);
    } catch (err) {
      setInviteLinkError(err.response?.data?.detail || 'Unable to create invite link');
    }
  };

  const revokeInviteLink = async (token) => {
    try {
      await orgAPI.revokeInvite(activeId, token);
      setInvites((prev) => prev.filter((i) => i.token !== token));
    } catch (err) {
      console.error(err);
    }
  };

  const copyInviteLink = (inv) => {
    navigator.clipboard?.writeText(inv.invite_url || '');
    setCopiedToken(inv.token);
    setTimeout(() => setCopiedToken(''), 2500);
  };

  const createOrganization = async () => {
    setNewOrgError('');
    if (!newOrgName.trim()) {
      setNewOrgError('Name is required');
      return;
    }
    try {
      const r = await orgAPI.create({
        name: newOrgName.trim(),
        contact_email: newOrgContact.trim() || null,
      });
      setNewOrgName('');
      setNewOrgContact('');
      await refreshOrganizations();
      setActiveId(r.data.id);
    } catch (err) {
      setNewOrgError(err.response?.data?.detail || 'Unable to create');
    }
  };

  if (ownerOrgs.length === 0 && !isAdmin()) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          You are not an owner of any organization. Ask an organization owner to grant you that role.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 0.5 }}>
        Organization
      </Typography>
      <Typography sx={{ color: '#838996', mb: 3 }}>
        Manage members, whitelist, and visibility for organizations you own.
      </Typography>

      {isAdmin() && (
        <Paper elevation={0} sx={{ border: '1px solid #e0ddd6', borderRadius: 2, p: 2.5, mb: 3 }}>
          <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 1 }}>
            Onboard a new organization (admin)
          </Typography>
          {newOrgError && <Alert severity="error" sx={{ mb: 1 }}>{newOrgError}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 1.5 }}>
            <TextField size="small" label="Name" value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} />
            <TextField size="small" label="Contact Email" value={newOrgContact} onChange={(e) => setNewOrgContact(e.target.value)} />
            <Button variant="contained" onClick={createOrganization}>Create</Button>
          </Box>
        </Paper>
      )}

      {ownerOrgs.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Organization</InputLabel>
            <Select label="Organization" value={activeId} onChange={(e) => setActiveId(e.target.value)}>
              {ownerOrgs.map((o) => (
                <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {loading || !detail ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      ) : (
        <Paper elevation={0} sx={{ border: '1px solid #e0ddd6', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
            <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', fontSize: '1.2rem' }}>
              {detail.name}
            </Typography>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1 }}>
              <Tab label="Settings" sx={{ textTransform: 'none' }} />
              <Tab label={`Whitelist (${whitelist.length})`} sx={{ textTransform: 'none' }} />
              <Tab label={`Members (${members.length})`} sx={{ textTransform: 'none' }} />
              <Tab label={`Invite Links (${invites.length})`} sx={{ textTransform: 'none' }} />
            </Tabs>
          </Box>
          <Divider />

          {tab === 0 && (
            <Box sx={{ p: 3 }}>
              <TextField
                fullWidth
                label="Description"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                multiline
                minRows={2}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Contact Email"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Visibility</InputLabel>
                <Select
                  label="Visibility"
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value)}
                >
                  <MenuItem value="own_only">Own only</MenuItem>
                  <MenuItem value="can_share">Can share</MenuItem>
                  <MenuItem value="see_all">See all</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ fontSize: '0.8rem', color: '#838996', mb: 2 }}>
                {visibilityLabels[visibility]}
              </Typography>
              <Button variant="contained" onClick={saveSettings} startIcon={<Save />}>
                Save
              </Button>
              {savedAt && (
                <Chip
                  icon={<CheckCircleOutline />}
                  label="Saved"
                  color="success"
                  size="small"
                  sx={{ ml: 1.5 }}
                />
              )}

              <Divider sx={{ my: 3 }} />

              <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 0.5 }}>
                AI-Controlled Inbox
              </Typography>
              <Typography sx={{ color: '#838996', fontSize: '0.82rem', mb: 1.5 }}>
                The Gmail account this organization uses to send PRA requests and receive replies. Record it during
                onboarding, then connect it once here.
              </Typography>
              {inboxError && <Alert severity="error" sx={{ mb: 1.5 }}>{inboxError}</Alert>}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {detail?.sender_account_id ? (
                  <Chip
                    icon={<MarkEmailRead sx={{ fontSize: 16 }} />}
                    label={`Connected${detail.inbox_email ? ` · ${detail.inbox_email}` : ''}`}
                    color="success"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<Email sx={{ fontSize: 16 }} />}
                    label={detail?.inbox_email ? `Not connected · ${detail.inbox_email}` : 'Not connected'}
                    color="default"
                    size="small"
                  />
                )}
                {!detail?.sender_account_id && (
                  <Button size="small" variant="outlined" onClick={connectInbox} startIcon={<Email />}>
                    Connect Inbox (Gmail)
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ color: '#838996', fontSize: '0.85rem', mb: 1.5 }}>
                Add an email address or a domain (e.g. <code>berkeley.edu</code>). Common consumer providers like
                gmail.com / outlook.com are blocked as domain entries — whitelist individual emails instead.
              </Typography>
              {whitelistError && <Alert severity="error" sx={{ mb: 1.5 }}>{whitelistError}</Alert>}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="name@example.com or example.com"
                  value={newWhitelist}
                  onChange={(e) => setNewWhitelist(e.target.value)}
                />
                <Button variant="contained" onClick={addWhitelistEntry}>Add</Button>
              </Box>
              <List dense>
                {whitelist.map((w) => (
                  <ListItem
                    key={w.id}
                    secondaryAction={
                      <Tooltip title="Remove">
                        <IconButton edge="end" onClick={() => removeWhitelistEntry(w.id)} size="small">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                    sx={{ border: '1px solid #e0ddd6', borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={w.value}
                      secondary={w.entry_type === 'domain' ? 'Domain' : 'Email'}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {tab === 2 && (
            <Box sx={{ p: 3 }}>
              {inviteError && <Alert severity="error" sx={{ mb: 1.5 }}>{inviteError}</Alert>}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Invite user by email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
                <Button variant="contained" onClick={inviteMember} startIcon={<PersonAdd />}>Invite</Button>
              </Box>
              <List dense>
                {members.map((m) => (
                  <ListItem
                    key={`${m.user_id}-${m.email}`}
                    secondaryAction={
                      m.user_id ? (
                        <Tooltip title="Remove member">
                          <IconButton edge="end" onClick={() => removeMember(m.user_id)} size="small">
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                    sx={{ border: '1px solid #e0ddd6', borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{m.full_name || m.username || m.email}</span>
                          {!m.user_id && <Chip size="small" label="Pending invite" />}
                          {m.role && <Chip size="small" label={m.role} variant="outlined" />}
                        </Box>
                      }
                      secondary={m.email}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {tab === 3 && (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ color: '#838996', fontSize: '0.85rem', mb: 1.5 }}>
                Invite links let anyone join this workspace — no admin approval needed. The link is single-use and
                expires after 7 days. Share it with the person you want to add.
              </Typography>
              {inviteLinkError && <Alert severity="error" sx={{ mb: 1.5 }}>{inviteLinkError}</Alert>}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    label="Role"
                    value={inviteLinkRole}
                    onChange={(e) => setInviteLinkRole(e.target.value)}
                  >
                    <MenuItem value="member">Member</MenuItem>
                    <MenuItem value="admin">Org Admin</MenuItem>
                    {detail?.my_role === 'owner' && <MenuItem value="owner">Owner</MenuItem>}
                  </Select>
                </FormControl>
                <Button variant="contained" onClick={generateInviteLink} startIcon={<LinkIcon />}>
                  Generate invite link
                </Button>
              </Box>
              <List dense>
                {invites.length === 0 && (
                  <Typography sx={{ color: '#838996', fontSize: '0.82rem', py: 2 }}>
                    No invite links yet — generate one above.
                  </Typography>
                )}
                {invites.map((inv) => {
                  const isCopied = copiedToken === inv.token;
                  return (
                    <ListItem
                      key={inv.token}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={isCopied ? 'Copied!' : 'Copy link'}>
                            <IconButton edge="end" onClick={() => copyInviteLink(inv)} size="small">
                              {isCopied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revoke">
                            <IconButton edge="end" onClick={() => revokeInviteLink(inv.token)} size="small">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      sx={{ border: '1px solid #e0ddd6', borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                              {inv.invite_url || inv.token}
                            </span>
                            <Chip size="small" label={inv.role} variant="outlined" />
                            {inv.redeemed_at && <Chip size="small" label="Used" color="success" />}
                          </Box>
                        }
                        secondary={inv.redeemed_at ? `Accepted ${new Date(inv.redeemed_at).toLocaleDateString()}` : `Expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default OrganizationPanel;
